import { useEffect, useMemo } from "react";
import { ProvetDepartment, Reason, ReasonGroup } from "../types";

interface UseProvetPreselectionProps {
  visitedClinicBookingProvetId?: number;
  visitedTreatmentProvetId?: number;
  departments?: ProvetDepartment[];
  reasons?: Reason[];
  reasonGroups?: ReasonGroup[];
  /** Whether the treatments are still loading */
  reasonsLoading?: boolean;
  /** The clinic chosen in Step 1, if any */
  selectedDepartmentId?: number;
  /** The treatment chosen in Step 2, if any */
  selectedReasonId?: number;
  setVisitedClinicBookingProvetId?: (id: number | undefined) => void;
  setVisitedTreatmentProvetId?: (id: number | undefined) => void;
}

/**
 * Whether a treatment belongs to a clinic: its reason group is one of the clinic's.
 * (The same URL matching the treatment list uses.)
 */
const isReasonOfDepartment = (
  reason: Reason,
  reasonGroups: ReasonGroup[],
  departmentId: number
): boolean =>
  reasonGroups.some(
    reasonGroup =>
      !!reasonGroup.department?.endsWith(`/${departmentId}/`) &&
      !!reason.group?.endsWith(`/${reasonGroup.id}/`)
  );

/**
 * The clinic and treatment preselected by a link to the booking page (?provetId= and
 * ?treatmentId=).
 *
 * The treatment is only preselected once a clinic has been chosen in Step 1, and only if it
 * is one of that clinic's treatments. So its Confirm and Change buttons never show while
 * Step 1 is open, and a treatment of another clinic can't be booked (observations 4 and 6).
 *
 * The linked treatment is remembered for the whole booking, and offered whenever the chosen
 * clinic offers it (also after changing the clinic). It is only forgotten when the customer
 * declines it by choosing a different treatment.
 */
export function useProvetPreselection({
  visitedClinicBookingProvetId,
  visitedTreatmentProvetId,
  departments,
  reasons,
  reasonGroups,
  reasonsLoading,
  selectedDepartmentId,
  selectedReasonId,
  setVisitedClinicBookingProvetId,
  setVisitedTreatmentProvetId
}: UseProvetPreselectionProps) {
  const preselectedClinic = useMemo(
    () => departments?.find(d => d.id === visitedClinicBookingProvetId),
    [departments, visitedClinicBookingProvetId]
  );

  const preselectedTreatment = useMemo(() => {
    if (!visitedTreatmentProvetId || !selectedDepartmentId) {
      return undefined;
    }

    const reason = reasons?.find(r => r.id === visitedTreatmentProvetId);

    return reason &&
      isReasonOfDepartment(reason, reasonGroups ?? [], selectedDepartmentId)
      ? reason
      : undefined;
  }, [reasons, reasonGroups, visitedTreatmentProvetId, selectedDepartmentId]);

  // A linked treatment can't be checked against the chosen clinic until the treatments have
  // loaded. Until then the treatment step shows the loading state, not the list, so it doesn't
  // render the list and then collapse into Confirm/Change.
  const isTreatmentPreselectionPending =
    !!visitedTreatmentProvetId && !!selectedDepartmentId && !!reasonsLoading;

  const clearPreselectedClinic = () => {
    setVisitedClinicBookingProvetId?.(undefined);
  };

  // Choosing a different treatment declines the linked one: forget it
  useEffect(() => {
    if (
      visitedTreatmentProvetId &&
      selectedReasonId &&
      selectedReasonId !== visitedTreatmentProvetId
    ) {
      setVisitedTreatmentProvetId?.(undefined);
    }
  }, [selectedReasonId, visitedTreatmentProvetId, setVisitedTreatmentProvetId]);

  return {
    preselectedClinic,
    preselectedTreatment,
    isTreatmentPreselectionPending,
    clearPreselectedClinic
  };
}
